import { useFormContext, useWatch } from 'react-hook-form';
import { AUSTRALIAN_STATES, GWPField, GWPInput, GWPSelect } from 'shared-components';
import ApplicationSection from '../ApplicationSection';
import { EntertainerApplicationFormState } from '../EntertainerApplicationTypes';
import { fieldError, useApplicationFormUpdate } from '../EntertainerApplicationFormUtils';

export default function ContactSection() {
	const { control, formState } = useFormContext<EntertainerApplicationFormState>();
	const update = useApplicationFormUpdate();
	const [firstName, lastName, email, phone] = useWatch({
		control,
		name: ['ContactFirstName', 'ContactLastName', 'ContactEmailAddress', 'ContactPrimaryPhoneNumber'],
	});
	const [street1, street2, city, state, postcode, abn] = useWatch({
		control,
		name: [
			'EntertainerStreetAddress1',
			'EntertainerStreetAddress2',
			'EntertainerAddressCity',
			'EntertainerAddressState',
			'EntertainerAddressPostcode',
			'EntertainerABN',
		],
	});

	return (
		<ApplicationSection
			title="Contact details"
			description="Who should we contact about bookings and application updates?"
		>
			<div className="entertainer-grid two-columns">
				<GWPField label="First name" required error={fieldError(formState.errors, 'ContactFirstName')}>
					<GWPInput
						id="ContactFirstName"
						value={firstName}
						onValueChange={(value) => update('ContactFirstName', value)}
						autoComplete="given-name"
					/>
				</GWPField>
				<GWPField label="Last name" required error={fieldError(formState.errors, 'ContactLastName')}>
					<GWPInput
						id="ContactLastName"
						value={lastName}
						onValueChange={(value) => update('ContactLastName', value)}
						autoComplete="family-name"
					/>
				</GWPField>
				<GWPField label="Email" required error={fieldError(formState.errors, 'ContactEmailAddress')}>
					<GWPInput
						id="ContactEmailAddress"
						type="email"
						value={email}
						onValueChange={(value) => update('ContactEmailAddress', value)}
						autoComplete="email"
					/>
				</GWPField>
				<GWPField
					label="Phone number"
					required
					error={fieldError(formState.errors, 'ContactPrimaryPhoneNumber')}
				>
					<GWPInput
						id="ContactPrimaryPhoneNumber"
						type="tel"
						value={phone}
						onValueChange={(value) => update('ContactPrimaryPhoneNumber', value)}
						autoComplete="tel"
					/>
				</GWPField>
			</div>
			<h3 className="entertainer-section-subheading">Address details</h3>
			<div className="entertainer-grid two-columns">
				<GWPField
					label="Street address 1"
					required
					error={fieldError(formState.errors, 'EntertainerStreetAddress1')}
				>
					<GWPInput
						id="EntertainerStreetAddress1"
						value={street1}
						autoComplete="address-line1"
						onValueChange={(value) => update('EntertainerStreetAddress1', value)}
					/>
				</GWPField>
				<GWPField label="Street address 2">
					<GWPInput
						id="EntertainerStreetAddress2"
						value={street2}
						autoComplete="address-line2"
						onValueChange={(value) => update('EntertainerStreetAddress2', value)}
					/>
				</GWPField>
				<GWPField
					label="Suburb or city"
					required
					error={fieldError(formState.errors, 'EntertainerAddressCity')}
				>
					<GWPInput
						id="EntertainerAddressCity"
						value={city}
						autoComplete="address-level2"
						onValueChange={(value) => update('EntertainerAddressCity', value)}
					/>
				</GWPField>
				<GWPField
					label="State or territory"
					required
					error={fieldError(formState.errors, 'EntertainerAddressState')}
				>
					<GWPSelect
						id="EntertainerAddressState"
						options={AUSTRALIAN_STATES}
						value={state || null}
						placeholder="Select state"
						onValueChange={(value) => update('EntertainerAddressState', value ?? '')}
					/>
				</GWPField>
				<GWPField label="Postcode" required error={fieldError(formState.errors, 'EntertainerAddressPostcode')}>
					<GWPInput
						id="EntertainerAddressPostcode"
						value={postcode}
						inputMode="numeric"
						autoComplete="postal-code"
						maxLength={4}
						onValueChange={(value) => update('EntertainerAddressPostcode', value)}
					/>
				</GWPField>
				<GWPField
					label="ABN"
					error={fieldError(formState.errors, 'EntertainerABN')}
					hint="Enter 11 digits; spaces are accepted."
				>
					<GWPInput
						id="EntertainerABN"
						value={abn}
						inputMode="numeric"
						onValueChange={(value) => update('EntertainerABN', value)}
					/>
				</GWPField>
			</div>
		</ApplicationSection>
	);
}
